import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../utils/echarts'
import styled from 'styled-components'
import { Loading } from '../../atoms/Loading'
import { Flex } from '../../atoms/Flex'
import { Box } from '../../atoms/Box'
import {
  PropertyCharacteristicsChartProps,
  DEFAULT_PROPERTY_CHARACTERISTICS_CHART_LABELS,
} from './PropertyCharacteristicsChart.types'

export interface PropertyCharacteristicsChartRef {
  exportChart: () => string | null
}

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`

const ChartWrapper = styled.div<{ $height: number }>`
  width: 100%;
  height: ${({ $height }) => $height}px;
`

const ErrorContainer = styled(Flex)`
  height: 350px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

const LoadingContainer = styled(Flex)`
  height: 350px;
`

const ChartsGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 100%;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }

  @media print {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`

export const PropertyCharacteristicsChart = forwardRef<
  PropertyCharacteristicsChartRef,
  PropertyCharacteristicsChartProps
>(
  (
    {
      propertyTypeData = [],
      buildingComplexityData = [],
      loading = false,
      error = null,
      height = 400,
      labels = {},
    },
    ref
  ) => {
    const mergedLabels = {
      ...DEFAULT_PROPERTY_CHARACTERISTICS_CHART_LABELS,
      ...labels,
    }
    const scatterChartRef = useRef<HTMLDivElement>(null)
    const complexityChartRef = useRef<HTMLDivElement>(null)
    const scatterChartInstance = useRef<echarts.ECharts | null>(null)
    const complexityChartInstance = useRef<echarts.ECharts | null>(null)

    useImperativeHandle(ref, () => ({
      exportChart: () => {
        if (scatterChartInstance.current) {
          return scatterChartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#fff',
          })
        }
        if (complexityChartInstance.current) {
          return complexityChartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#fff',
          })
        }
        return null
      },
    }))

    useEffect(() => {
      if (
        !loading &&
        !error &&
        propertyTypeData.length > 0 &&
        scatterChartRef.current
      ) {
        initializeScatterChart()
      }

      return () => {
        scatterChartInstance.current?.dispose()
      }
    }, [propertyTypeData, loading, error])

    useEffect(() => {
      if (
        !loading &&
        !error &&
        buildingComplexityData.length > 0 &&
        complexityChartRef.current
      ) {
        initializeComplexityChart()
      }

      return () => {
        complexityChartInstance.current?.dispose()
      }
    }, [buildingComplexityData, loading, error])

    useEffect(() => {
      const handleResize = () => {
        scatterChartInstance.current?.resize()
        complexityChartInstance.current?.resize()
      }

      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }, [])

    const formatCurrency = (value: number) => {
      if (value >= 1e9) return `CHF ${(value / 1e9).toFixed(1)}B`
      if (value >= 1e6) return `CHF ${(value / 1e6).toFixed(1)}M`
      if (value >= 1e3) return `CHF ${(value / 1e3).toFixed(0)}K`
      return `CHF ${value.toFixed(0)}`
    }

    const formatArea = (value: number) => {
      return `${Math.round(value).toLocaleString()} m²`
    }

    const initializeScatterChart = () => {
      if (
        !scatterChartRef.current ||
        !propertyTypeData ||
        propertyTypeData.length === 0
      )
        return

      if (scatterChartInstance.current) {
        scatterChartInstance.current.dispose()
      }

      scatterChartInstance.current = safeChartInit(scatterChartRef.current)

      // Prepare data for scatter plot
      const scatterData = propertyTypeData.map((item) => ({
        value: [
          item.avg_surface_area || 0,
          item.avg_price || 0,
          item.transaction_count || 0,
        ],
        name: item.legal_type || 'Unknown',
      }))

      const option: echarts.EChartsOption = {
        title: {
          text: mergedLabels.performanceMatrix,
          subtext: mergedLabels.performanceMatrixSubtext,
          left: 'center',
          top: 10,
          textStyle: {
            fontSize: 16,
            fontWeight: 600,
          },
          subtextStyle: {
            fontSize: 12,
          },
        },
        tooltip: {
          trigger: 'item',
          formatter: (params: any) => {
            const data = params.data
            return `
            <div style="padding: 8px;">
              <div style="font-weight: 600; margin-bottom: 8px;">${data.name}</div>
              <div>${mergedLabels.avgSurfaceArea}: ${formatArea(data.value[0])}</div>
              <div>${mergedLabels.avgPrice}: ${formatCurrency(data.value[1])}</div>
              <div>${mergedLabels.transactions}: ${data.value[2].toLocaleString()}</div>
            </div>
          `
          },
        },
        grid: {
          left: '12%',
          right: '10%',
          top: '20%',
          bottom: '15%',
        },
        xAxis: {
          type: 'value',
          name: mergedLabels.avgSurfaceArea,
          nameLocation: 'middle',
          nameGap: 30,
          nameTextStyle: {
            fontSize: 12,
            fontWeight: 600,
          },
          axisLabel: {
            formatter: (value: number) => formatArea(value),
          },
        },
        yAxis: {
          type: 'value',
          name: mergedLabels.avgPrice,
          nameLocation: 'middle',
          nameGap: 50,
          nameTextStyle: {
            fontSize: 12,
            fontWeight: 600,
          },
          axisLabel: {
            formatter: (value: number) => formatCurrency(value),
          },
        },
        series: [
          {
            type: 'scatter',
            data: scatterData,
            symbolSize: (data: any) => {
              // Size based on transaction count (min 10, max 50)
              const count = data[2]
              const maxCount = Math.max(
                ...propertyTypeData.map((d) => d.transaction_count)
              )
              return Math.max(10, Math.min(50, (count / maxCount) * 50))
            },
            itemStyle: {
              color: (params: any) => {
                // Color by property type
                const colors = [
                  '#5470c6',
                  '#91cc75',
                  '#fac858',
                  '#ee6666',
                  '#73c0de',
                  '#3ba272',
                  '#fc8452',
                  '#9a60b4',
                ]
                return colors[params.dataIndex % colors.length]
              },
              opacity: 0.8,
            },
            emphasis: {
              itemStyle: {
                opacity: 1,
                borderWidth: 2,
                borderColor: '#333',
              },
            },
            label: {
              show: true,
              position: 'top',
              formatter: (params: any) => params.data.name,
              fontSize: 10,
            },
          },
        ],
      }

      scatterChartInstance.current.setOption(option)
    }

    const initializeComplexityChart = () => {
      if (
        !complexityChartRef.current ||
        !buildingComplexityData ||
        buildingComplexityData.length === 0
      )
        return

      if (complexityChartInstance.current) {
        complexityChartInstance.current.dispose()
      }

      complexityChartInstance.current = safeChartInit(
        complexityChartRef.current
      )

      // Sort data by floor count
      const sortedData = [...buildingComplexityData].sort(
        (a, b) => a.floor_count - b.floor_count
      )

      const option: echarts.EChartsOption = {
        title: {
          text: mergedLabels.complexityIndex,
          subtext: mergedLabels.complexityIndexSubtext,
          left: 'center',
          top: 10,
          textStyle: {
            fontSize: 16,
            fontWeight: 600,
          },
          subtextStyle: {
            fontSize: 12,
          },
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
          },
          formatter: (params: any) => {
            let result = `<div style="padding: 8px;"><div style="font-weight: 600; margin-bottom: 8px;">${params[0].axisValue} Floors</div>`
            params.forEach((param: any) => {
              const value =
                param.seriesName === 'Average Price'
                  ? formatCurrency(param.value)
                  : formatArea(param.value)
              result += `<div>${param.marker} ${param.seriesName}: ${value}</div>`
            })
            result += `</div>`
            return result
          },
        },
        legend: {
          data: [mergedLabels.avgPrice!, mergedLabels.avgSurfaceArea!],
          top: '12%',
          left: 'center',
        },
        grid: {
          left: '10%',
          right: '10%',
          top: '25%',
          bottom: '15%',
        },
        xAxis: {
          type: 'category',
          data: sortedData.map((d) => d.floor_count.toString()),
          name: mergedLabels.numberOfFloors,
          nameLocation: 'middle',
          nameGap: 30,
          nameTextStyle: {
            fontSize: 12,
            fontWeight: 600,
          },
        },
        yAxis: [
          {
            type: 'value',
            name: mergedLabels.avgPrice,
            position: 'left',
            nameTextStyle: {
              fontSize: 12,
              fontWeight: 600,
            },
            axisLabel: {
              formatter: (value: number) => formatCurrency(value),
            },
          },
          {
            type: 'value',
            name: mergedLabels.avgSurfaceAreaM2,
            position: 'right',
            nameTextStyle: {
              fontSize: 12,
              fontWeight: 600,
            },
            axisLabel: {
              formatter: (value: number) => formatArea(value),
            },
          },
        ],
        series: [
          {
            name: mergedLabels.avgPrice,
            type: 'bar',
            data: sortedData.map((d) => d.avg_price),
            itemStyle: {
              color: '#5470c6',
            },
          },
          {
            name: mergedLabels.avgSurfaceArea,
            type: 'line',
            yAxisIndex: 1,
            data: sortedData.map((d) => d.avg_surface_area),
            itemStyle: {
              color: '#91cc75',
            },
            lineStyle: {
              width: 3,
            },
            smooth: true,
          },
        ],
      }

      complexityChartInstance.current.setOption(option)
    }

    if (loading) {
      return (
        <LoadingContainer justify="center" align="center">
          <Loading size="large" />
        </LoadingContainer>
      )
    }

    if (error) {
      return (
        <ErrorContainer justify="center" align="center">
          {error}
        </ErrorContainer>
      )
    }

    if (propertyTypeData.length === 0 && buildingComplexityData.length === 0) {
      return (
        <ErrorContainer justify="center" align="center">
          {mergedLabels.noData}
        </ErrorContainer>
      )
    }

    return (
      <ChartContainer>
        <ChartsGrid>
          {propertyTypeData.length > 0 && (
            <ChartWrapper $height={height} ref={scatterChartRef} />
          )}
          {buildingComplexityData.length > 0 && (
            <ChartWrapper $height={height} ref={complexityChartRef} />
          )}
        </ChartsGrid>
      </ChartContainer>
    )
  }
)

PropertyCharacteristicsChart.displayName = 'PropertyCharacteristicsChart'
